import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DynamicDrawUsage,
  Float32BufferAttribute,
  Group,
  Mesh,
  Object3D,
  Points,
  PointsMaterial,
  Shader,
  Texture,
  TextureLoader,
  Vector3,
} from "three";

import { SceneObjectBase } from "../object";
import { defaultScale } from "../../util/models";
import { PoserClock } from "../../util/clock";
import { Context } from "../../context";
import { almostEqual } from "../../../lib/misc/util";

import fanColors from "../../../../data/stadium/fan.json";

export type Occupancy = "low" | "mid" | "high" | "fixed" | "none";

const up = new Vector3(0, 1, 0);

export class Crowd extends SceneObjectBase {
  object: Object3D;

  private contextRef: Context;

  private occupancy: Occupancy;

  private crowdGeom: Mesh;
  private separation: number;

  private geometry?: BufferGeometry;
  // make these interleaved
  private attributes?: {
    position: Float32BufferAttribute;
    timer: Float32BufferAttribute;
    state: Float32BufferAttribute;
    color: Float32BufferAttribute;
  };

  private materialShader?: Shader;

  private defaultHeight = 0.1;

  private initFov: number;

  private prevT?: number;

  private colorGen: ColorGenerator;

  constructor(
    context: Context,
    crowdGeom: Mesh,
    homeAbbr: string = "LAD",
    awayAbbr: string = "SF",
    separation = 0.4
  ) {
    super();

    const { camera, options, state } = context;

    this.contextRef = context;

    const sprite = new TextureLoader().load(
      `${options.assetBase}/images/circle.png`
    );

    this.colorGen = new ColorGenerator(homeAbbr, awayAbbr);

    this.crowdGeom = crowdGeom;
    this.separation = separation;
    this.occupancy = options.stadiumOccupancy ?? "high";

    this.initFov = camera.fov;

    const pointMat = new PointsMaterial({
      size: 2.5,
      vertexColors: true,
      map: sprite,
      alphaTest: 0.5,
      transparent: true,
    });

    pointMat.onBeforeCompile = (shader: Shader) => {
      shader.uniforms.time = { value: 0 };
      shader.uniforms.maxHeight = { value: this.defaultHeight };
      shader.uniforms.maxTime = { value: state.crowdSpeed };
      shader.uniforms.fovScale = { value: 1 };

      const declarations = `
          uniform float time;
          uniform float maxHeight;
          uniform float maxTime;
          uniform float fovScale;

          attribute float timer;
          attribute float state;
        `;

      shader.vertexShader =
        declarations +
        shader.vertexShader.replace(
          "#include <begin_vertex>",
          `
            vec3 transformed = vec3(position);

            float dt = time - timer;

            if ((timer == 0.0f) || (abs(dt) > maxTime))
            {
              // settle
              if (state > 0.5f)
              {
                transformed.y = transformed.y + maxHeight;
              }
            }
            else
            {
              // interpolate
              // float t_mod = (dt - (maxTime * floor(dt / maxTime))) / maxTime;
              float t_mod = dt / maxTime;
              float alpha = -(cos(t_mod * PI) - 1.0f) / 2.0f;

              if (state > 0.5f)
              {
                // stand up
                transformed.y = transformed.y + (maxHeight * alpha);
              }
              else
              {
                // sit down
                transformed.y = transformed.y + (maxHeight * (1.0f - alpha));
              }
            }
          `
        );

      shader.vertexShader = shader.vertexShader.replace(
        "gl_PointSize = size;",
        `
            gl_PointSize = size * fovScale;
          `
      );

      this.materialShader = shader;
    };

    const dots = this.makeDots(pointMat);
    if (dots) {
      this.object = dots;
    } else {
      this.object = new Group();
    }
  }

  update(clock: PoserClock) {
    if (!this.geometry || !this.materialShader || !this.attributes) {
      return;
    }

    // TODO: determine if this is required
    this.materialShader.uniforms.fovScale.value =
      this.initFov / this.contextRef.camera.getEffectiveFOV();

    if (clock.relative == this.prevT) {
      return;
    }

    const maxTime = 1 / this.contextRef.state.crowdSpeed;

    const t = clock.relative;
    this.materialShader.uniforms.time.value = t;
    this.materialShader.uniforms.maxTime.value = maxTime;

    const reset =
      this.prevT !== undefined && Math.abs(clock.relative - this.prevT) > 2;

    const energyTest = this.contextRef.state.crowdEnergy * 0.01;
    // TODO: do all this in the shader
    for (let i = 0; i < this.attributes.position.count; i++) {
      if (reset) {
        this.attributes.timer.setX(i, 0);
        this.attributes.state.setX(i, Math.random() > 0.5 ? 0 : 1);

        this.prevT = undefined;
      } else {
        const timer = this.attributes.timer.getX(i);

        // const finished = timer == 0;
        const finished = Math.abs(t - timer) > maxTime;
        if (finished && Math.random() < energyTest) {
          // standup/down
          const state = this.attributes.state.getX(i);
          this.attributes.state.setX(i, state ? 0 : 1);

          this.attributes.timer.setX(i, t);
        }
      }
    }

    this.attributes.state.needsUpdate = true;
    this.attributes.timer.needsUpdate = true;

    this.prevT = clock.relative;
  }

  private makeDots(pointMat: PointsMaterial): Object3D | undefined {
    const occupancy =
      this.occupancy == "low"
        ? 0.04
        : this.occupancy == "mid"
        ? 0.1
        : this.occupancy == "high"
        ? 0.8
        : 0;

    if (this.occupancy == "fixed") {
      return this.crowdGeom;
    }

    this.crowdGeom.visible = false;

    const nonIndexed = this.crowdGeom.geometry.toNonIndexed() as BufferGeometry;

    const buffer: BufferAttribute = nonIndexed.attributes[
      "position"
    ] as BufferAttribute;

    const vertices = [];
    const colors = [];
    const states = [];
    const timers = [];

    const triPoints = [new Vector3(), new Vector3(), new Vector3()];
    const temp = new Vector3();

    const back = new Vector3();

    let hasPrevious = false;

    let topEdgeCnt = 0;

    for (let i = 0; i + 3 < buffer.count; i += 3) {
      triPoints[0].set(buffer.getX(i), buffer.getY(i), buffer.getZ(i));
      triPoints[1].set(
        buffer.getX(i + 1),
        buffer.getY(i + 1),
        buffer.getZ(i + 1)
      );
      triPoints[2].set(
        buffer.getX(i + 2),
        buffer.getY(i + 2),
        buffer.getZ(i + 2)
      );

      const topEdge = getTopEdgeIndices(triPoints.map((p) => p.y));

      if (topEdge) {
        topEdgeCnt++;

        const first = triPoints[topEdge[0]];
        const second = triPoints[topEdge[1]];

        back.subVectors(second, first).cross(up).normalize();

        const distance = first.distanceTo(second);
        const increments = this.separation / distance;
        if (increments < 0.5) {
          for (let alpha = 0; alpha <= 1; alpha += increments) {
            if (
              Math.random() > occupancy &&
              (!hasPrevious || Math.random() < 0.25)
            ) {
              hasPrevious = false;
              continue;
            }

            const randOffset = (Math.random() - 0.5) * 0.01;
            alpha += randOffset;
            temp.lerpVectors(first, second, alpha);

            const randBackOffset = (Math.random() - 0.5) * 0.08;
            temp.addScaledVector(back, 0.2 + randBackOffset);

            temp.y -= 1;

            vertices.push(temp.x, temp.y, temp.z);

            colors.push(...this.colorGen.generate(), 1);

            timers.push(0);
            states.push(0);

            hasPrevious = true;
          }
        }
      }
    }

    const missingRows = buffer.count / 6 - topEdgeCnt;
    if (missingRows > 0) {
      console.warn(`missing ${missingRows} crowd rows`);
    }

    // TODO: make these buffers interleaved
    this.attributes = {
      position: new Float32BufferAttribute(vertices, 3),
      timer: new Float32BufferAttribute(timers, 1),
      state: new Float32BufferAttribute(states, 1),
      color: new Float32BufferAttribute(colors, 4),
    };

    this.geometry = new BufferGeometry();
    this.geometry.setAttribute("position", this.attributes.position);
    this.geometry.setAttribute("timer", this.attributes.timer);
    this.geometry.setAttribute("state", this.attributes.state);
    this.geometry.setAttribute("color", this.attributes.color);

    this.geometry.computeBoundingSphere();

    (<BufferAttribute>this.geometry.attributes.position).setUsage(
      DynamicDrawUsage
    );

    const points = new Points(this.geometry, pointMat);
    points.receiveShadow = true;

    points.scale.set(defaultScale, defaultScale, defaultScale);

    const group = new Group();
    group.add(points);

    return group;
  }
}

function getTopEdgeIndices(
  yCoords: number[],
  tolerance = 0.8
): [number, number] | undefined {
  const pairs = new Map([
    [0, [1, 2]],
    [1, [0, 2]],
  ]);

  for (const [check, against] of pairs) {
    if (
      almostEqual(yCoords[check], yCoords[against[0]], tolerance) &&
      yCoords[check] > yCoords[against[1]]
    ) {
      return [check, against[0]];
    } else if (
      almostEqual(yCoords[check], yCoords[against[1]], tolerance) &&
      yCoords[check] > yCoords[against[0]]
    ) {
      return [check, against[1]];
    }
  }

  return undefined;
}

interface ColorChance {
  colors: Color[];
  chance?: number;
}

class ColorGenerator {
  colors: {
    colors: Color[];
    test: number;
  }[];

  private keyedColors: {
    home: ColorChance[];
    away: ColorChance[];
    other: ColorChance[];
  } = {
    home: [
      { colors: [], chance: 0.185 },
      { colors: [], chance: 0.155 },
      { colors: [], chance: 0.12 },
      { colors: [], chance: 0.08 },
      { colors: [], chance: 0.05 },
    ],
    away: [
      { colors: [], chance: 0.13 },
      { colors: [], chance: 0.095 },
      { colors: [], chance: 0.075 },
      { colors: [], chance: 0.045 },
      { colors: [], chance: 0.02 },
    ],
    other: [
      {
        colors: [0xb8b6b0, 0xaeb2a6, 0xffffff, 0xf5f5f5, 0xa4afae].map(
          (c) => new Color(c)
        ),
      },
    ],
  };

  private color: Color = new Color();

  constructor(home: string, away: string) {
    const homeColors: string[] =
      (fanColors[home]?.length || 0) >= 3 ? fanColors[home] : fanColors.LAD;
    const awayColors: string[] =
      (fanColors[away]?.length || 0) >= 3 ? fanColors[away] : fanColors.SF;
    for (let i = 0; i < homeColors.length; i++) {
      this.keyedColors.home[i].colors.push(
        new Color(Number.parseInt(homeColors[i], 16))
      );
    }
    for (let i = 0; i < awayColors.length; i++) {
      this.keyedColors.away[i].colors.push(
        new Color(Number.parseInt(awayColors[i], 16))
      );
    }

    this.colors = [];

    this.resetColors();
  }

  private resetColors() {
    let accum = 0;
    const initColors = [
      ...this.keyedColors.home,
      ...this.keyedColors.away,
      ...this.keyedColors.other,
    ];
    for (const color of initColors) {
      accum = color.chance !== undefined ? accum + color.chance : 1;

      if (accum > 1) {
        console.error("color chance accumulator saturated");
      }

      this.colors.push({
        colors: color.colors.map((c) => {
          const col = new Color(c);
          col.convertSRGBToLinear();
          return col;
        }),
        test: accum,
      });
    }
  }

  generate(): [number, number, number] {
    if (Math.random() > 0.4) {
      const test = Math.random();

      for (const color of this.colors) {
        if (test < color.test) {
          this.color = chooseRandom(color.colors);
          break;
        }
      }
    }

    if (Math.random() < 0.0005) {
      this.resetColors();
    }

    // 5% luminousity change - normally distributed
    const dL = (randomNormal() - 0.5) * 0.02;
    this.color = changeLS(this.color, 1 + dL);

    return [this.color.r, this.color.g, this.color.b];
  }
}

function chooseRandom<T>(list: T[]): T {
  return list[Math.floor(Math.random() * list.length)];
}

function changeLS(color: Color, relL = 1, relS = 0.9999999): Color {
  const hsl = { h: 0, s: 0, l: 0 };
  color.getHSL(hsl);
  color.setHSL(hsl.h, hsl.s * relS, hsl.l * relL);

  return color;
}

// from https://stackoverflow.com/questions/25582882/javascript-math-random-normal-distribution-gaussian-bell-curve
function randomNormal(): number {
  let u = 0,
    v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  let num = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  num = num / 10.0 + 0.5;
  if (num > 1 || num < 0) return randomNormal();
  return num;
}
